export const hasBodyClass = (className) => {
  return document.body.classList.contains(className);
};

export function formatPrice(priceInCents) {
  const price = priceInCents / 100;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(price);
}

export function formatPriceWithoutTrailingZeros(price) {
  let formattedPrice = formatPrice(price);

  if (price % 1 === 0) {
    formattedPrice = formattedPrice.replace('.00', '');
  }

  return formattedPrice;
}

export function interpolateTranslation(translations, key, variable, value) {
  if (!translations) return value;
  return translations[key].replace(`{{ ${variable} }}`, value);
}

export function calculateHeight() {
  let totalHeaderHeight = 0;
  const headergroupElements = document.querySelectorAll(
    '.shopify-section-group-header-group'
  );

  headergroupElements.forEach((element, index) => {
    const rect = element.getBoundingClientRect(); // Get the element's position and size relative to the viewport
    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    );
    totalHeaderHeight += visibleHeight;
  });

  const fixedHeaderContainer = document.querySelector(
    '.header-container.fixed'
  );

  if (fixedHeaderContainer) {
    const rect = fixedHeaderContainer.getBoundingClientRect();
    totalHeaderHeight += rect.height;
  }

  return totalHeaderHeight;
}

export function findMetafield(metafields, key) {
  if (!metafields || !Array.isArray(metafields)) return;

  const field = metafields?.find((field) => field.key === key);
  return Array.isArray(field?.value) ? field?.value[0] : field?.value;
}

async function calculateCartDiscount(product, productApplicableDiscounts) {
  if (!productApplicableDiscounts) return null;

  const cart = await window?.Alpine?.store('cart')?.getCart();
  const cartDiscountCode = cart?.attributes?.discount_code;
  const discountCodeWithValue = productApplicableDiscounts.split(',').find(item => item.includes(cartDiscountCode));

  if (discountCodeWithValue) {
    const [discountCode, discountValue] = discountCodeWithValue.split('|');
    const isDiscountApplied = cart?.discount_codes.find(
      (discount) => discount.code === discountCode
    );

    if (isDiscountApplied) {
      const savings = Number(discountValue);
      const productPrice = ((product.price * ((100 - savings) / 100)) / 100).toFixed(2);

      return {
        productPrice,
        savings,
        singlePrice: product.price,
        quantityInBundle: 1,
        discountType: 'cart'
      }
    }
  }

  return null;
}

function calculateBundleDiscount(product, singleItemPrice, quantity) {
  if (singleItemPrice && quantity) {
    const singlePrice = Number(singleItemPrice || 0) * 100;
    const quantityInBundle = Number(quantity || 0);
    const originalPrice = singlePrice * quantityInBundle;
    const decimalSavings = originalPrice - product.price;
    const validatedSavings = decimalSavings > 0 ? decimalSavings : 0;
    const bundleSavingsPercentage = (validatedSavings / originalPrice) * 100;
    
    return {
      productPrice: (Number(product.price) / 100).toFixed(2),
      savings: bundleSavingsPercentage,
      singlePrice,
      quantityInBundle,
      isBundle: true,
      discountType: 'bundle'
    };
  }

  return null;
}

function getDefaultPricing(product, singlePrice, quantityInBundle) {
  return {
    productPrice: (Number(product.price) / 100).toFixed(2),
    savings: 0,
    singlePrice: singlePrice || null,
    quantityInBundle: quantityInBundle || null,
    isBundle: false,
    discountType: null
  };
}

export function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

export function   stripHtmlTags(htmlString) {
    if (!htmlString) return '';
    const div = document.createElement('div');
    div.innerHTML = htmlString;
    return div.textContent || div.innerText || '';
}

export async function calculatePrices(
  product,
  singleItemPrice,
  quantity,
  product_related_discount_codes
) {
  if (!product) return;

  const metafields = product.metafields || [];
  const productApplicableDiscounts = product_related_discount_codes
    ? product_related_discount_codes
    : findMetafield(metafields, 'product_related_discount_codes');
  
  const bundle = product.productBundle?.references?.edges[0]?.node?.fields || [];
  const singlePrice = singleItemPrice !== undefined
    ? singleItemPrice
    : findMetafield(bundle, 'single_item_price');

  const quantityInBundle = quantity
    ? quantity
    : findMetafield(metafields, 'product-z');

  const cartDiscount = await calculateCartDiscount(product, productApplicableDiscounts);
  const bundleDiscount = calculateBundleDiscount(product, singlePrice, quantityInBundle);

  if (cartDiscount && bundleDiscount) {
    return cartDiscount.savings > bundleDiscount.savings
      ? cartDiscount
      : bundleDiscount;

  } else if (cartDiscount) {
    return cartDiscount;

  } else if (bundleDiscount) {
    return bundleDiscount;
  }

  return getDefaultPricing(product, singlePrice, quantityInBundle);
}

export function decodeHtmlEntities(text) {
  if (!text) return "";
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
}

/**
 * Converts a string to a Shopify-style handle (lowercase, hyphens, no special chars)
 * @param {string} str
 * @returns {string}
 */
export function handleize(str) {
  return str
    .toLowerCase()
    .replace(/['"]/g, '') 
    .replace(/[^a-z0-9]+/g, '-') 
    .replace(/^-+|-+$/g, '') 
    .replace(/-+/g, '-'); 
}

export default {
  /**
   * Emit a custom event
   * @param  {String} type   The event type
   * @param  {Object} detail Any details to pass along with the event
   * @param  {Node}   elem   The element to attach the event to
   */
  emitEvent(type, detail = {}, elem = document) {
    if (!type) return;

    let event = new CustomEvent(type, {
      bubbles: true,
      cancelable: true,
      detail: detail
    });

    return elem.dispatchEvent(event);
  },

  getShopDomain() {
    return Shopify.shop;
  },

  parseRichTextJSONToHTML(jsonString) {
    if (!jsonString) return '';

    try {
      const obj = JSON.parse(jsonString);
      return obj.children
        .map((paragraph) => {
          const text = paragraph.children.map((child) => child.value).join('');
          return `<p>${text.trim()}</p>`;
        })
        .join('');
    } catch (e) {
      console.error('Invalid JSON:', e);
      return '';
    }
  },

  getDomain(use_local = true, use_shop = false) {
    if (use_local) {
      if (
        location.origin.includes('127.0.0.1') ||
        location.origin.includes('localhost')
      ) {
        return location.origin;
      }
    }
    if (use_shop) return `https://${Shopify.shop}`;
    return `https://${window.location.host}`;
  },

  randomNumber(min = 0, max = 1000) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  },

  debounce(callback, wait) {
    let timeoutId = null;

    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        callback(...args);
      }, wait);
    };
  },

  truncateLongTitle(input) {
    return input.length > 5 ? `${input.substring(0, 18)}...` : input;
  },

  async fetchHTML(endpoint) {
    return await fetch(endpoint)
      .then((response) => response.text())
      .then((responseText) => {
        return new DOMParser().parseFromString(responseText, 'text/html');
      });
  },
  getCookie(name) {
    // Create a regex to find the cookie by name
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }

    return null; // Return null if the cookie is not found
  },
  setCookie(name, value, days = 30) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; expires=${expires}; path=/`;
  },
  updateUrlParameters(originalUrl, params) {
    const url = new URL(originalUrl.toString());

    for (const [key, value] of Object.entries(params)) {
      if (!value) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value.toString());
      }
    }

    return url.toString();
  },
  dispatchCustomEvent(eventName, detail = { message: '' }) {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  },

  setCSSVariable(attr, val) {
    document.querySelector(':root').style.setProperty(attr, val);
  },

  getImageSize(originalUrl, width, height = null, queries = null) {
    const params = {
      width: Math.ceil(width),
      height: Math.ceil(height),
      ...queries
    };

    if (!originalUrl.startsWith('https:')) {
      originalUrl = `https:${originalUrl}`;
    }

    return this.updateUrlParameters(originalUrl, params);
  },

  generateUUID() {
    return uuidv4();
  },

  isVideo(mediaSrc) {
    return mediaSrc?.includes('mp4');
  },

  appendUrlParam(key, val) {
    const url = new URL(window.location);
    url.searchParams.set(key, val);
    window.history.pushState({}, '', url);
  },

  removeUrlParam(key) {
    const url = new URL(window.location);
    url.searchParams.delete(key);
    window.history.pushState({}, '', url);
  },

  currencyFromCents(val, digits = 2) {
    return `$${(val / 100).toFixed(digits)}`;
  },

  getDayName(dateStr, locale) {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, { weekday: 'long' });
  },

  arrayToEnum(array, keyField) {
    return array.reduce((obj, item) => {
      obj[item[keyField]] = item;
      return obj;
    }, {});
  },

  isValidEmail(email) {
    // Define a regular expression for validating an Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Test the email against the regular expression
    return emailRegex.test(email);
  },

  isValidPhoneNumber(phoneNumber) {
    // Define a regular expression for validating a phone number with spaces, dashes, or parentheses
    const phoneRegex = /^\+?([0-9]{1,3})?[\s\-]?[1-9][0-9\s\-\(\)]{6,}$/;

    // Test the phone number against the regular expression
    return phoneRegex.test(phoneNumber);
  },
  leastCommonMultiple(min, max) {
    function range(min, max) {
      let arr = [];
      for (let i = min; i <= max; i++) {
        arr.push(i);
      }
      return arr;
    }

    function gcd(a, b) {
      return !b ? a : gcd(b, a % b);
    }

    function lcm(a, b) {
      return (a * b) / gcd(a, b);
    }

    let multiple = min;
    range(min, max).forEach(function (n) {
      multiple = lcm(multiple, n);
    });

    return multiple;
  },

  isActiveLink(link) {
    const linkParams = new URLSearchParams(`?${link.split('?')[1]}`);
    const currentParams = new URLSearchParams(window.location.search);
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (currentUrl === link) return true;

    if (currentUrl.includes('/account/orders/') && linkParams.get('view') === 'orders') {
      return true;
    }

    if (!currentParams.has('view')) {
      return false;
    }

    return linkParams.get('view') === currentParams.get('view');
  },

  getObjectFromKeyAndValue(array, key, value) {
    return array.find((obj) => obj[key] === value);
  },

  // Only work with strings or numbers
  twoArraysWithSameValues(arr1, arr2) {
    return [...arr1].sort().join(',') === [...arr2].sort().join(',');
  },

  formatTimestamp(dateString) {
    const date = new Date(dateString);

    const month = date.toLocaleString('default', { month: 'long' });
    const day = date.getDay();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  },

  toggleClass(el, className, isAdd) {
    if (isAdd) {
      el.classList.add(className);
    } else {
      el.classList.remove(className);
    }
  },

  updateUrlAndFilters(filters) {
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    let queryStringUrl = '';

    if (searchParams.has('q')) {
      const currentQValue = searchParams.get('q');
      queryStringUrl = `?q=${currentQValue}&${filters}`;
    } else {
      queryStringUrl = `?${filters}`;
    }

    const newUrl = `${queryStringUrl}`;

    window.history.pushState(null, '', newUrl);

    return newUrl;
  },

  renderAccentText(template, parts, color) {
    let result = template;

    if (!parts) return result;

    const parsedParts = JSON.parse(parts.replaceAll(`'`, `"`));

    for (const part of parsedParts) {
      result = result.replace(
        part,
        `<span style="color: ${color};">${part}</span>`
      );
    }

    return result;
  },



  isTransparent(color) {
    const rgba = color.match(/rgba?\(([^)]+)\)/);
    if (rgba) {
      const alpha = parseFloat(rgba[1].split(',')[3]);
      return alpha === 0;
    }

    return false;
  }
};


